/**
 * Parse and validate one Excel A1 cell address without relying on permissive
 * regular expressions. Supports optional sheet qualification and `$` markers,
 * and enforces Excel's real row/column limits.
 */

export interface ParsedSingleCellAddress {
  column: number;
  row: number;
  normalizedAddress: string;
}

export interface ParsedCellRangeAddress {
  startColumn: number;
  startRow: number;
  endColumn: number;
  endRow: number;
  rowCount: number;
  columnCount: number;
  normalizedAddress: string;
}

export const EXCEL_MAX_COLUMN = 16_384; // XFD
export const EXCEL_MAX_ROW = 1_048_576;

function isAsciiLetter(code: number): boolean {
  return (
    (code >= 65 && code <= 90) ||
    (code >= 97 && code <= 122)
  );
}

function isAsciiDigit(code: number): boolean {
  return code >= 48 && code <= 57;
}

/**
 * Returns parsed coordinates for a single cell, or `undefined` when the input
 * is a range, malformed, or outside Excel's worksheet bounds.
 */
export function parseSingleCellAddress(
  value: unknown,
): ParsedSingleCellAddress | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  if (!trimmed) return undefined;

  const separator = trimmed.lastIndexOf("!");
  if (separator >= 0 && !trimmed.slice(0, separator).trim()) {
    return undefined;
  }
  const leaf = trimmed.slice(separator + 1).trim();
  if (!leaf) return undefined;

  let cursor = 0;
  if (leaf[cursor] === "$") cursor += 1;

  const columnStart = cursor;
  while (
    cursor < leaf.length &&
    isAsciiLetter(leaf.charCodeAt(cursor))
  ) {
    cursor += 1;
  }
  if (cursor === columnStart) return undefined;

  const columnText = leaf.slice(columnStart, cursor).toUpperCase();
  if (leaf[cursor] === "$") cursor += 1;

  const rowStart = cursor;
  while (
    cursor < leaf.length &&
    isAsciiDigit(leaf.charCodeAt(cursor))
  ) {
    cursor += 1;
  }
  if (cursor === rowStart || cursor !== leaf.length) return undefined;

  const rowText = leaf.slice(rowStart);
  if (rowText[0] === "0") return undefined;
  const row = Number(rowText);
  if (!Number.isSafeInteger(row) || row < 1 || row > EXCEL_MAX_ROW) {
    return undefined;
  }

  let column = 0;
  for (let index = 0; index < columnText.length; index += 1) {
    column = column * 26 + (columnText.charCodeAt(index) - 64);
    if (column > EXCEL_MAX_COLUMN) return undefined;
  }

  return {
    column,
    row,
    normalizedAddress: `${columnText}${row}`,
  };
}

export function isSingleCellAddress(value: unknown): boolean {
  return parseSingleCellAddress(value) !== undefined;
}

/**
 * Format one-based Excel coordinates as a normalized A1 address.
 */
export function formatCellAddress(
  column: number,
  row: number,
): string | undefined {
  if (
    !Number.isInteger(column) ||
    !Number.isInteger(row) ||
    column < 1 ||
    column > EXCEL_MAX_COLUMN ||
    row < 1 ||
    row > EXCEL_MAX_ROW
  ) {
    return undefined;
  }

  let remaining = column;
  let letters = "";
  while (remaining > 0) {
    const remainder = (remaining - 1) % 26;
    letters = String.fromCharCode(65 + remainder) + letters;
    remaining = Math.floor((remaining - 1) / 26);
  }
  return `${letters}${row}`;
}

/**
 * Parse a single-cell or rectangular A1 range. Sheet qualification is
 * accepted on the first address, while the returned coordinates remain
 * worksheet-local. Reversed ranges are rejected because row-major citation
 * matrices require an unambiguous top-left anchor.
 */
export function parseCellRangeAddress(
  value: unknown,
): ParsedCellRangeAddress | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  if (!trimmed) return undefined;

  const separator = trimmed.lastIndexOf("!");
  if (separator >= 0 && !trimmed.slice(0, separator).trim()) {
    return undefined;
  }
  const leaf = trimmed.slice(separator + 1).trim();
  if (!leaf) return undefined;

  const colon = leaf.indexOf(":");
  if (colon !== leaf.lastIndexOf(":")) return undefined;

  const startText = colon >= 0 ? leaf.slice(0, colon).trim() : leaf;
  const endText = colon >= 0 ? leaf.slice(colon + 1).trim() : leaf;
  const start = parseSingleCellAddress(startText);
  const end = parseSingleCellAddress(endText);
  if (!start || !end) return undefined;
  if (start.column > end.column || start.row > end.row) return undefined;

  return {
    startColumn: start.column,
    startRow: start.row,
    endColumn: end.column,
    endRow: end.row,
    rowCount: end.row - start.row + 1,
    columnCount: end.column - start.column + 1,
    normalizedAddress:
      start.normalizedAddress === end.normalizedAddress
        ? start.normalizedAddress
        : `${start.normalizedAddress}:${end.normalizedAddress}`,
  };
}
